/** Mirrors EPN.PayrollConfig.Api's Tax Payment Settings overview (EPN-156). Read-only: derived on the server from the
 * company's work locations and employee assignments; no tax setup data exists yet. */
export interface TaxRelatedLocation {
  id: string;
  name: string;
  status: string;
  /** "123 Market St, San Francisco, CA 94105", when the work location service returned one. */
  address?: string | null;
  employeesAssigned?: number;
}

export interface TaxJurisdictionRow {
  jurisdictionKey: string;
  jurisdiction: string;
  setupType: string;
  relatedLocations: TaxRelatedLocation[];
  employeesAssigned: number;
  /** One of SETUP_STATUSES. Never says Ready, Active or Verified: EPN does not verify tax setup. */
  setupStatus: string;
  /** Short names of the components of this state's detailed setup (for example SUI, ETT, SDI, Deposit). Absent when there is none yet. */
  taxComponents?: string[] | null;
  hasDetailedSetup?: boolean;
  /** No source of verification exists yet, so this is always null. */
  verifiedOn?: string | null;
}

/** The statuses the server can derive today, in filter order. */
export const SETUP_STATUSES = [
  'Setup Needed',
  'Setup Needed - No Employees Assigned Yet',
  'Setup Recorded - Not Verified',
  'Needs Tax Review',
  'Historical Only',
] as const;

export interface TaxLocationNeedingReview {
  id: string;
  name: string;
  reason: string;
}

export interface TaxPaymentSettingsOverview {
  rows: TaxJurisdictionRow[];
  locationsNeedingReview: TaxLocationNeedingReview[];
  /** Last four digits of the company EIN. The full number never reaches the browser; absent when none is on file. */
  companyEinLast4?: string | null;
}

/** EPN-154 T2: employer-recorded details for one jurisdiction, effective-dated. Account numbers arrive masked; the full
 * number is never returned by the server. */
export interface EmployerTaxDetailEntry {
  effectiveFrom: string;
  withholdingAccountNumberMasked: string | null;
  suiAccountNumberMasked: string | null;
  federalDepositSchedule: string | null;
  filingFrequency: string | null;
  recordedAtUtc: string;
}

export interface EmployerTaxDetails {
  jurisdictionKey: string;
  jurisdiction: string;
  current: EmployerTaxDetailEntry | null;
  history: EmployerTaxDetailEntry[];
}

/** Null account numbers are carried forward by the server; an empty string clears one. */
export interface SaveEmployerTaxDetailRequest {
  effectiveFrom: string;
  withholdingAccountNumber?: string | null;
  suiAccountNumber?: string | null;
  federalDepositSchedule?: number | null;
  filingFrequency?: number | null;
}

export const federalDepositScheduleOptions = [
  { value: 1, label: 'Monthly' },
  { value: 2, label: 'Semiweekly' },
] as const;

export const filingFrequencyOptions = [
  { value: 1, label: 'Monthly' },
  { value: 2, label: 'Quarterly' },
  { value: 3, label: 'Annually' },
] as const;

// ---- State tax setup (rates and deposit schedule), schema-driven so another state can be added without changing the screen ----

export interface StateTaxScheduleOption {
  code: string;
  label: string;
}

export interface StateTaxComponentDefinition {
  key: string;
  name: string;
  abbreviation: string;
  /** "rate" or "schedule". */
  kind: 'rate' | 'schedule';
  fieldLabel: string;
  tableLabel: string;
  options: StateTaxScheduleOption[];
  /** A warning shown while the component has no value in force. It only says the employer has not entered the value yet. */
  missingAdvisory?: string | null;
  /** The component's chip label in the list when it differs from `abbreviation` (the drawer badge). */
  chipLabel?: string | null;
  /** Set when each rate entry also carries an annual cap in dollars (NY PFL): the label of the cap input and of its table column. */
  capFieldLabel?: string | null;
  capTableLabel?: string | null;
  /** True for a component that applies only to some employers or locations (MCTMT): it need not be recorded before a review. */
  isConditional?: boolean;
}

export interface StateTaxTabDefinition {
  key: string;
  label: string;
  componentKeys: string[];
  /** "Needs Tax Review" for a draft program that takes no entries yet; `description` says what must be determined first. */
  reviewStatus?: string | null;
  description?: string | null;
}

export interface StateTaxSchema {
  components: StateTaxComponentDefinition[];
  tabs: StateTaxTabDefinition[];
  /** Says plainly that values are recorded as stated and not checked against any agency. */
  disclosure: string;
  /** "Needs Tax Review" when the state's programs are only a draft candidate list; such a state cannot be marked as reviewed. */
  reviewStatus?: string | null;
  reviewNote?: string | null;
}

export interface StateTaxEntry {
  id: string;
  componentKey: string;
  ratePercent: number | null;
  /** The annual cap in dollars, for components that have one. */
  annualCapAmount?: number | null;
  scheduleCode: string | null;
  scheduleLabel: string | null;
  effectiveFrom: string;
  recordedAtUtc: string;
  recordedBy: string;
  isCurrent: boolean;
  isFuture: boolean;
}

export interface StateTaxComponentState {
  componentKey: string;
  entries: StateTaxEntry[];
}

export interface StateTaxSetup {
  jurisdictionKey: string;
  jurisdiction: string;
  /** Null for a state with no detailed setup yet. */
  schema: StateTaxSchema | null;
  components: StateTaxComponentState[];
  /** The latest "Mark as reviewed", or null. An employer-side review, not an agency check. */
  review?: StateTaxReview | null;
}

export interface StateTaxReview {
  reviewedAtUtc: string;
  reviewedBy: string;
  /** True when an entry was added or withdrawn after the review, so it must not be shown as current. */
  changedSince: boolean;
}

export interface AddStateTaxEntryRequest {
  componentKey: string;
  ratePercent?: number | null;
  scheduleCode?: string | null;
  annualCapAmount?: number | null;
  effectiveFrom: string;
}

export interface StateTaxHistoryItem {
  entryId: string;
  componentKey: string;
  componentAbbreviation: string;
  value: string;
  effectiveFrom: string;
  action: 'Added' | 'Removed';
  occurredAtUtc: string;
  by: string;
}
